import { getMinimumRequiredVersion, isForceUpdateEnabled } from './config';
import { VersionInfo, getCurrentVersionInfo, isUpdateRequired } from './version';

export interface ForceUpdateState {
  isBlocked: boolean;
  currentVersion: VersionInfo;
  requiredVersion?: string;
  updateUrl?: string;
  message?: string;
}

export interface ForceUpdateOptions {
  onForceUpdate?: (state: ForceUpdateState) => void;
  onUpdateAvailable?: (state: ForceUpdateState) => void;
  customUpdateUrl?: string;
  customMessage?: string;
}

class ForceUpdateService {
  private isBlocked = false;
  private currentState: ForceUpdateState | null = null;
  private options: ForceUpdateOptions = {};

  /**
   * Check if app should be blocked due to force update
   */
  public async checkForceUpdate(options: ForceUpdateOptions = {}): Promise<ForceUpdateState> {
    this.options = options;
    
    // Only check if force update is enabled
    if (!isForceUpdateEnabled()) {
      return this.getDefaultState();
    }

    try {
      const state = await this.performForceUpdateCheck();
      this.currentState = state;
      
      if (state.isBlocked) {
        this.isBlocked = true;
        this.options.onForceUpdate?.(state);
      } else if (state.requiredVersion) {
        this.options.onUpdateAvailable?.(state);
      }

      return state;
    } catch (error) {
      console.error('Force update check failed:', error);
      return this.getDefaultState();
    }
  }

  /**
   * Perform the actual force update check
   */
  private async performForceUpdateCheck(): Promise<ForceUpdateState> {
    const currentVersion = getCurrentVersionInfo();
    const minimumRequired = getMinimumRequiredVersion();
    
    // Check against minimum required version
    if (isUpdateRequired(currentVersion.version, minimumRequired)) {
      return {
        isBlocked: true,
        currentVersion,
        requiredVersion: minimumRequired,
        updateUrl: this.options.customUpdateUrl || this.getDefaultUpdateUrl(),
        message: this.options.customMessage || 'A new version is required to continue using the app.'
      };
    }

    // Check against server version if available
    try {
      const serverVersion = await this.fetchServerVersion();
      if (serverVersion && serverVersion.forceUpdate) {
        if (isUpdateRequired(currentVersion.version, serverVersion.version)) {
          return {
            isBlocked: true,
            currentVersion,
            requiredVersion: serverVersion.version,
            updateUrl: serverVersion.updateUrl || this.options.customUpdateUrl || this.getDefaultUpdateUrl(),
            message: serverVersion.message || this.options.customMessage || 'A critical update is required to continue using the app.'
          };
        }
      }
    } catch (error) {
      console.warn('Could not fetch server version for force update check:', error);
    }

    return {
      isBlocked: false,
      currentVersion
    };
  }

  /**
   * Fetch server version info
   */
  private async fetchServerVersion(): Promise<VersionInfo | null> {
    try {
      // This would typically fetch from your version endpoint
      // For now, we'll return null to indicate no server check
      return null;
    } catch (error) {
      console.warn('Failed to fetch server version:', error);
      return null;
    }
  }

  /**
   * Get default update URL
   */
  private getDefaultUpdateUrl(): string {
    // Return appropriate update URL based on platform
    if (typeof window !== 'undefined') {
      const userAgent = window.navigator.userAgent.toLowerCase();
      
      if (userAgent.includes('android')) {
        return 'https://play.google.com/store/apps/details?id=com.holifya.experts';
      } else if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
        return 'https://apps.apple.com/app/holifya-experts/id123456789';
      }
    }
    
    return 'https://app.holifya.com';
  }

  /**
   * Get default state
   */
  private getDefaultState(): ForceUpdateState {
    return {
      isBlocked: false,
      currentVersion: getCurrentVersionInfo()
    };
  }

  /**
   * Check if app is currently blocked
   */
  public isAppBlocked(): boolean {
    return this.isBlocked;
  }

  /**
   * Get current force update state
   */
  public getCurrentState(): ForceUpdateState | null {
    return this.currentState;
  }

  /**
   * Reset force update state
   */
  public reset(): void {
    this.isBlocked = false;
    this.currentState = null;
  }

  /**
   * Manually trigger force update check
   */
  public async triggerCheck(options: ForceUpdateOptions = {}): Promise<ForceUpdateState> {
    return this.checkForceUpdate(options);
  }
}

// Export singleton instance
export const forceUpdateService = new ForceUpdateService();

/**
 * Check for force update
 */
export function checkForceUpdate(options: ForceUpdateOptions = {}): Promise<ForceUpdateState> {
  return forceUpdateService.checkForceUpdate(options);
}

/**
 * Check if app is blocked by force update
 */
export function isAppBlocked(): boolean {
  return forceUpdateService.isAppBlocked();
}

/**
 * Get current force update state
 */
export function getForceUpdateState(): ForceUpdateState | null {
  return forceUpdateService.getCurrentState();
}

/**
 * Reset force update state
 */
export function resetForceUpdate(): void {
  forceUpdateService.reset();
}

/**
 * Trigger manual force update check
 */
export function triggerForceUpdateCheck(options: ForceUpdateOptions = {}): Promise<ForceUpdateState> {
  return forceUpdateService.triggerCheck(options);
} 